/**
 * 明阳智能 - 项目管理接口（task_name: mingyang_project）
 * 协议来源：Web管理后台-检查项配置.md
 */
import { callMingyangTask } from './client'

const TASK = 'mingyang_project' as const

// ------------------------------------------------------------
// 通用类型
// ------------------------------------------------------------

export type ProjectStatus = 0 | 1

export interface ExportTaskBrief {
  export_id: number
  name: string
}

export interface ExportTaskDetail {
  export_id: number
  name: string
  status: 0 | 1 | 2 // 0-处理中 1-已完成 2-失败
  status_label: string
  row_count: number
  download_url: string
  start_time: string
  end_time: string
  error_msg?: string
}

// ------------------------------------------------------------
// 4.1 项目统计
// ------------------------------------------------------------

export interface ProjectStatsResponse {
  total_projects: number
  active_projects: number
  total_machine_numbers: number
}

export function fetchProjectStats(dispatch: (...args: any[]) => any) {
  return callMingyangTask<ProjectStatsResponse>(dispatch, TASK, 'stats', {})
}

// ------------------------------------------------------------
// 4.2 项目列表
// ------------------------------------------------------------

export interface ProjectListParams {
  project_name?: string
  base_name?: string
  status?: ProjectStatus | null
  page?: number
  page_size?: number
}

export interface ProjectListItem {
  project_id: number
  project_name: string
  base_name: string
  project_numbers: string
  knowledge_count: number
  /** 关联检查项 ID 列表（逗号分隔），后端 list 接口附带返回，前端反向聚合时用 */
  knowledges?: string
  operator_name: string
  update_time: string
  status: ProjectStatus
}

export interface ProjectListResponse {
  total: number
  page: number
  page_size: number
  list: ProjectListItem[]
}

export async function fetchProjectList(
  dispatch: (...args: any[]) => any,
  params: ProjectListParams = {}
): Promise<ProjectListResponse> {
  const data = await callMingyangTask<ProjectListResponse>(
    dispatch,
    TASK,
    'list',
    {
      project_name: params.project_name || '',
      base_name: params.base_name || '',
      status: params.status ?? null,
      page: params.page || 1,
      page_size: params.page_size || 20
    }
  )
  // 字段兜底：id ↔ project_id；last_operator_name ↔ operator_name；
  // project_numbers/knowledges 可能 null 归一为空串；knowledge_count 默认 0
  return {
    ...data,
    list: ((data && data.list) || []).map((r: any) => ({
      ...r,
      project_id: r.project_id ?? r.id,
      operator_name: r.operator_name ?? r.last_operator_name ?? '',
      project_numbers: r.project_numbers ?? '',
      knowledges: r.knowledges ?? '',
      knowledge_count: r.knowledge_count ?? 0
    }))
  }
}

// ------------------------------------------------------------
// 4.3 项目详情
// ------------------------------------------------------------

export interface ProjectKnowledgeItem {
  knowledge_id: number
  inspection: string
  status: 0 | 1
}

export interface ProjectDetailResponse {
  project_id: number
  project_name: string
  base_name: string
  project_numbers: string
  knowledges: string
  status: ProjectStatus
  create_time: string
  update_time: string
  knowledge_list: ProjectKnowledgeItem[]
}

export async function fetchProjectDetail(
  dispatch: (...args: any[]) => any,
  project_id: number
): Promise<ProjectDetailResponse> {
  // 后端入参字段是 id，同时兼容 project_id
  const d = await callMingyangTask<any>(dispatch, TASK, 'detail', {
    id: project_id,
    project_id
  })
  // 字段兜底：id ↔ project_id；knowledges/numbers 可能 null
  return {
    ...d,
    project_id: d?.project_id ?? d?.id,
    project_numbers: d?.project_numbers ?? '',
    knowledges: d?.knowledges ?? '',
    knowledge_list: Array.isArray(d?.knowledge_list)
      ? d.knowledge_list.map((k: any) => ({
          ...k,
          knowledge_id: k.knowledge_id ?? k.id
        }))
      : []
  }
}

// ------------------------------------------------------------
// 4.4 创建项目
// ------------------------------------------------------------

export interface CreateProjectParams {
  project_name: string
  base_name: string
  project_numbers: string
  knowledges: string
  group_id: string
  user_id: string
}

export function createProject(
  dispatch: (...args: any[]) => any,
  params: CreateProjectParams
) {
  return callMingyangTask<{ project_id: number }>(
    dispatch,
    TASK,
    'create',
    params
  )
}

// ------------------------------------------------------------
// 4.5 更新项目
// ------------------------------------------------------------

export interface UpdateProjectParams {
  project_id: number
  project_name?: string
  base_name?: string
  project_numbers?: string
  knowledges?: string
  group_id: string
  user_id: string
}

export function updateProject(
  dispatch: (...args: any[]) => any,
  params: UpdateProjectParams
) {
  // 后端入参字段是 id，同时兼容 project_id
  return callMingyangTask<{ success: boolean }>(dispatch, TASK, 'update', {
    ...params,
    id: params.project_id
  })
}

// ------------------------------------------------------------
// 4.6 停用/启用项目
// ------------------------------------------------------------

export interface ToggleProjectStatusParams {
  project_id: number
  status: ProjectStatus
  group_id: string
  user_id: string
}

export function toggleProjectStatus(
  dispatch: (...args: any[]) => any,
  params: ToggleProjectStatusParams
) {
  return callMingyangTask<{
    success: boolean
    status_before: ProjectStatus
    status_after: ProjectStatus
  }>(dispatch, TASK, 'toggle_status', {
    ...params,
    id: params.project_id
  })
}

// ------------------------------------------------------------
// 4.7 发起导出
// ------------------------------------------------------------

export interface ProjectExportParams {
  project_name?: string
  base_name?: string
  status?: ProjectStatus | null
  start_time?: string
  end_time?: string
  group_id: string
  user_id: string
}

export function startProjectExport(
  dispatch: (...args: any[]) => any,
  params: ProjectExportParams
) {
  return callMingyangTask<ExportTaskBrief>(dispatch, TASK, 'export', {
    project_name: params.project_name || '',
    base_name: params.base_name || '',
    status: params.status ?? null,
    start_time: params.start_time || '',
    end_time: params.end_time || '',
    group_id: params.group_id,
    user_id: params.user_id
  })
}

// ------------------------------------------------------------
// 4.7.1 导出状态查询
// ------------------------------------------------------------

export function fetchProjectExportStatus(
  dispatch: (...args: any[]) => any,
  export_id: number
) {
  return callMingyangTask<ExportTaskDetail>(dispatch, TASK, 'export_status', {
    id: export_id,
    export_id
  })
}

// ------------------------------------------------------------
// 4.7.2 导出历史列表
// ------------------------------------------------------------

export interface ProjectExportListResponse {
  total: number
  list: ExportTaskDetail[]
}

export function fetchProjectExportList(
  dispatch: (...args: any[]) => any,
  params: { user_id: string; page?: number; page_size?: number }
) {
  return callMingyangTask<ProjectExportListResponse>(
    dispatch,
    TASK,
    'export_list',
    {
      user_id: params.user_id,
      page: params.page || 1,
      page_size: params.page_size || 10
    }
  )
}

// ------------------------------------------------------------
// 4.9 筛选下拉选项
// ------------------------------------------------------------

export interface ProjectFilterOptionsResponse {
  base_name_options: string[]
  status_options: { value: number; label: string }[]
}

export function fetchProjectFilterOptions(dispatch: (...args: any[]) => any) {
  return callMingyangTask<ProjectFilterOptionsResponse>(
    dispatch,
    TASK,
    'filter_options',
    {}
  )
}
